import React, { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { GroupedNavSidebar } from './GroupedNavSidebar';

export interface INavSection {
  key: string;
  label: string;
  icon?: React.ReactNode;
  content: React.ReactNode;
  /** Ausblenden (z. B. Reiter nur im Template-Modus). */
  hidden?: boolean;
  /** Deaktiviert klickbar, aber ausgegraut (z. B. Platzhalter). */
  disabled?: boolean;
}

interface IProps {
  /** Kopfbereich oben (Hauptinformationen). */
  header?: React.ReactNode;
  sections: INavSection[];
  /** Startreiter (key); sonst erster sichtbarer. */
  initialKey?: string;
  /** Überschrift der Menügruppe links (wie im Vorgang gruppiert). */
  navTitle?: string;
}

/**
 * Layout „Kopf oben / Menü links / Inhalt rechts" (#1244). Das linke Menü nutzt
 * dieselbe `GroupedNavSidebar` wie der Vorgang, damit die Optik identisch ist.
 * Der aktive Reiter wird intern gehalten; ausgeblendete Reiter (`hidden`)
 * erscheinen nicht im Menü.
 */
export const SectionNavLayout: React.FC<IProps> = ({
  header,
  sections,
  initialKey,
  navTitle = 'Bereiche',
}) => {
  const visible = useMemo(() => sections.filter(s => !s.hidden), [sections]);
  const [activeKey, setActiveKey] = useState<string>(
    initialKey && visible.some(s => s.key === initialKey)
      ? initialKey
      : (visible[0]?.key ?? '')
  );

  const active = visible.find(s => s.key === activeKey) ?? visible[0] ?? null;

  const navSections = [
    {
      title: navTitle,
      items: visible.map(s => ({
        value: s.key,
        label: s.label,
        icon: s.icon ?? null,
        disabled: s.disabled,
      })),
    },
  ];

  return (
    <Box>
      {header && <Box sx={{ mb: 2 }}>{header}</Box>}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Box sx={{ width: 240, flexShrink: 0 }}>
          <GroupedNavSidebar<string>
            value={active?.key ?? ''}
            onChange={setActiveKey}
            sections={navSections}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, mt: 2 }}>{active?.content}</Box>
      </Box>
    </Box>
  );
};
